import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Message from '@/design_system/Message';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCancel from '@/icons/Cancel.svg';
import {
  ArticleCancellationReason,
  CLIENT_ARTICLE_CANCELLATION_REASONS,
  STORE_ARTICLE_CANCELLATION_REASONS,
  useValidateOnBehalfOfClient,
} from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { ArticleActionProps } from './ArticleActions';

export const ValidateOnBehalfOfClientAction = ({ onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();
  const { workflow, view: requestView } = useRequestContext();
  const { article, state, view } = useArticleContext();

  const [apiError, setApiError] = useState<string | null>(null);
  const [isOpenRefuseDialog, setIsOpenRefuseDialog] = useState(false);

  const chooseArticleServiceTraits = workflow?.nodes.find(
    (node) => 'step' in node && node.step === 'service-choice'
  )?.config?.tasks?.choose_article_service?.traits;

  // If the user that will perform the validation on behalf of the client
  // did not have the possibility to review the workshop quote before,
  // then they may directly refuse the workshop quote.
  const refusalTypes: RefusalType[] = chooseArticleServiceTraits?.includes('store')
    ? ['client']
    : ['store', 'client'];

  const {
    mutateAsync: validateOnBehalfOfClient,
    isPending: isValidateOnBehalfOfClientPending,
    isSuccess: isValidateOnBehalfOfClientSuccess,
  } = useValidateOnBehalfOfClient({
    articleId: article.id,
  });

  const handleValidation = async () => {
    setApiError(null);

    const { hasError } = state.errors.check({
      services: {
        actions: {
          actions: {
            missingDescription: view.services.actions.fields.description.editable,
            missingOrganizationPrice:
              requestView.organizationPrice.shown &&
              view.services.actions.fields.organizationPrice.editable &&
              view.services.actions.fields.organizationPrice.required,
            organizationPriceUpdateInProgress:
              requestView.organizationPrice.shown &&
              view.services.actions.fields.organizationPrice.editable,
            spareParts: view.services.actions.fields.spareParts.shown
              ? {
                  missingOrganizationPrice:
                    requestView.organizationPrice.shown &&
                    view.services.actions.fields.spareParts.organizationPrice.editable &&
                    view.services.actions.fields.spareParts.organizationPrice.required,
                  organizationPriceUpdateInProgress:
                    requestView.organizationPrice.shown &&
                    view.services.actions.fields.spareParts.organizationPrice.editable,
                }
              : undefined,
          },
        },
      },
    });

    if (hasError) {
      return;
    }

    try {
      await validateOnBehalfOfClient({ value: 'accepted' });
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <Footer>
        {apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <Button
            variant="secondary-danger"
            size="medium"
            onPress={() => setIsOpenRefuseDialog(true)}
          >
            <IconCancel />
            {refusalTypes.length === 1 ? (
              <Trans id="article.actions.validate-on-behalf-of-client.refusal.supervisor">
                Client refusal
              </Trans>
            ) : (
              <Trans id="article.actions.validate-on-behalf-of-client.refusal">Refuse</Trans>
            )}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={handleValidation}
            isLoading={isValidateOnBehalfOfClientPending || isValidateOnBehalfOfClientSuccess}
          >
            {refusalTypes.length === 1 ? (
              <Trans id="article.actions.validate-on-behalf-of-client.validation.supervisor">
                Client validation
              </Trans>
            ) : (
              <Trans id="article.actions.validate-on-behalf-of-client.validation">Validate</Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
      <RefuseValidationDialog
        isOpen={isOpenRefuseDialog}
        setIsOpen={setIsOpenRefuseDialog}
        onActionDone={onActionDone}
        refusalTypes={refusalTypes}
      />
    </>
  );
};

export const RefuseValidationDialog = ({
  isOpen,
  setIsOpen,
  onActionDone,
  refusalTypes,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
  refusalTypes: RefusalType[];
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      title={
        refusalTypes.length === 1 ? (
          refusalTypes[0] === 'client' ? (
            <Trans id="article.actions.validate-on-behalf-of-client.dialog.title.client">
              Client refusal
            </Trans>
          ) : (
            <Trans id="article.actions.validate-on-behalf-of-client.dialog.title.store">
              Workshop quote refusal
            </Trans>
          )
        ) : (
          <Trans id="article.actions.validate-on-behalf-of-client.dialog.title">Refusal</Trans>
        )
      }
    >
      <RefuseValidationDialogContent onActionDone={onActionDone} refusalTypes={refusalTypes} />
    </Dialog>
  );
};

type RefusalType = 'client' | 'store';

const RefuseValidationDialogContent = ({
  onActionDone,
  refusalTypes,
}: {
  onActionDone: () => void;
  refusalTypes: RefusalType[];
}) => {
  const { t, i18n } = useLingui();
  const { article } = useArticleContext();

  const [apiError, setApiError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [refusalType, setRefusalType] = useState(
    refusalTypes.length === 1 ? refusalTypes[0] : undefined
  );
  const [reason, setReason] = useState<ArticleCancellationReason | undefined>();
  const [otherReason, setOtherReason] = useState<string>();

  const hasError =
    !reason || ((reason === 'other' || reason === 'store-other') && !otherReason?.trim());

  const storeRefusalTypeLabel = t({
    id: 'article.actions.validate-on-behalf-of-client.dialog.refusal-type.store',
    message: 'I refuse the quote from the workshop',
  });

  const clientRefusalTypeLabel = t({
    id: 'article.actions.validate-on-behalf-of-client.dialog.refusal-type.client',
    message: 'The client refuses the quote',
  });

  const otherReasonLabel = t({
    id: 'article.actions.validate-on-behalf-of-client.dialog.client-refusal-reason.other.label',
    message: 'Specify the reason',
  });

  const {
    mutateAsync: validateOnBehalfOfClient,
    isPending: isValidateOnBehalfOfClientPending,
    isSuccess: isValidateOnBehalfOfClientSuccess,
  } = useValidateOnBehalfOfClient({
    articleId: article.id,
  });

  const handleRefusal = async () => {
    setApiError(null);
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      await validateOnBehalfOfClient({
        value: 'refused',
        reason,
        otherReason: otherReason?.trim(),
      });
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Paragraph color="secondary">
            {refusalTypes.length === 1 ? (
              refusalTypes[0] === 'client' ? (
                <Trans id="article.actions.validate-on-behalf-of-client.dialog.text.client">
                  You're about to refuse the quote on behalf of the client. Please confirm their
                  response and specify their refusal reason:
                </Trans>
              ) : (
                <Trans id="article.actions.validate-on-behalf-of-client.dialog.text.store">
                  You're about to refuse the quote from the workshop. Please specify the refusal
                  reason:
                </Trans>
              )
            ) : (
              <Trans id="article.actions.validate-on-behalf-of-client.dialog.text">
                Select which quote you are refusing:
              </Trans>
            )}
          </Paragraph>

          {refusalTypes.length > 1 && (
            <RadioBoxGroup
              name="refusal-type"
              ariaLabel={t({
                id: 'article.actions.validate-on-behalf-of-client.dialog.refusal-type.label',
                message: 'Who is refusing',
              })}
              isInvalid={showErrors && !refusalType}
              value={refusalType}
              onChange={(newRefusalType) => {
                setRefusalType(newRefusalType);
                setReason(undefined);
                setOtherReason(undefined);
              }}
              direction="column"
            >
              <RadioBox
                key="store"
                value="store"
                ariaLabel={storeRefusalTypeLabel}
                title={storeRefusalTypeLabel}
              />
              <RadioBox
                key="client"
                value="client"
                ariaLabel={clientRefusalTypeLabel}
                title={clientRefusalTypeLabel}
              />
            </RadioBoxGroup>
          )}

          {!!refusalType && (
            <Stack gap="0.5rem">
              {refusalTypes.length > 1 && (
                <p className="label-100-medium">
                  <Trans id="article.actions.validate-on-behalf-of-client.dialog.reason.label">
                    Please specify the reason
                  </Trans>
                </p>
              )}

              <RadioBoxGroup
                name="refusal-reason"
                ariaLabel={t({
                  id: 'article.actions.validate-on-behalf-of-client.dialog.client-refusal-reason.label',
                  message: 'Refusal reason',
                })}
                isInvalid={showErrors && !reason}
                value={reason}
                onChange={(value) => {
                  setReason(value);
                  setOtherReason(undefined);
                }}
                direction="column"
              >
                {(refusalType === 'client'
                  ? CLIENT_ARTICLE_CANCELLATION_REASONS
                  : STORE_ARTICLE_CANCELLATION_REASONS
                ).map((option) => {
                  const label = 'onBehalfLabel' in option ? option.onBehalfLabel : option.label;

                  return (
                    <RadioBox
                      key={option.id}
                      value={option.id}
                      ariaLabel={i18n._(label)}
                      title={i18n._(label)}
                    />
                  );
                })}
              </RadioBoxGroup>

              {(reason === 'other' || reason === 'store-other') && (
                <TextArea
                  ariaLabel={otherReasonLabel}
                  placeholder={otherReasonLabel}
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  isInvalid={showErrors && !otherReason?.trim()}
                  rows={3}
                />
              )}
            </Stack>
          )}

          {showErrors && hasError && (
            <Message type="error">
              <Trans id="article.actions.validate-on-behalf-of-client.dialog.error">
                Please specify the refusal reason
              </Trans>
            </Message>
          )}
        </Stack>
      </main>
      <Footer>
        {apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <Button
            variant="danger"
            size="medium"
            onPress={handleRefusal}
            isLoading={isValidateOnBehalfOfClientPending || isValidateOnBehalfOfClientSuccess}
          >
            <Trans id="article.actions.validate-on-behalf-of-client.dialog.refuse">Refuse</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
